import { useCallback, useMemo } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { SubscriptionFeature } from '../types/subscription';

interface UseFeatureGateResult {
  // Проверка доступа к фиче
  hasFeature: boolean;
  // Общее состояние подписки
  hasActiveSubscription: boolean;
  isTrialing: boolean;
  trialDaysRemaining: number | null;
  // Загрузка
  loading: boolean;
  // Действия
  redirectToCheckout: (planId: string, billingInterval: 'monthly' | 'yearly') => Promise<void>;
  redirectToPortal: (returnUrl?: string) => Promise<void>;
}

export const useFeatureGate = (feature: SubscriptionFeature): UseFeatureGateResult => {
  const {
    hasFeature: checkFeature,
    hasActiveSubscription,
    isTrialing,
    trialDaysRemaining,
    loading,
    redirectToCheckout,
    redirectToPortal,
  } = useSubscription();

  const hasFeature = useMemo(() => checkFeature(feature), [checkFeature, feature]);

  return {
    hasFeature,
    hasActiveSubscription,
    isTrialing,
    trialDaysRemaining,
    loading,
    redirectToCheckout,
    redirectToPortal,
  };
};

// Хук для проверки нескольких фич сразу
export const useMultipleFeatures = (features: SubscriptionFeature[]): {
  hasAllFeatures: boolean;
  hasAnyFeature: boolean;
  featureStatus: Record<SubscriptionFeature, boolean>;
  loading: boolean;
} => {
  const { hasFeature: checkFeature, loading } = useSubscription();

  const featureStatus = useMemo(() => {
    return features.reduce((acc, feature) => {
      acc[feature] = checkFeature(feature);
      return acc;
    }, {} as Record<SubscriptionFeature, boolean>);
  }, [features, checkFeature]);

  const hasAllFeatures = useMemo(() => {
    return features.every(feature => checkFeature(feature));
  }, [features, checkFeature]);

  const hasAnyFeature = useMemo(() => {
    return features.some(feature => checkFeature(feature));
  }, [features, checkFeature]);

  return {
    hasAllFeatures,
    hasAnyFeature,
    featureStatus,
    loading,
  };
};

// Хук для получения информации о триале
export const useTrialInfo = (): {
  isTrialing: boolean;
  trialDaysRemaining: number | null;
  trialExpired: boolean;
  loading: boolean;
} => {
  const { isTrialing, trialDaysRemaining, trialInfo, loading } = useSubscription();

  const trialExpired = useMemo(() => {
    return trialInfo?.hasExpiredTrial ?? false;
  }, [trialInfo]);

  return {
    isTrialing,
    trialDaysRemaining,
    trialExpired,
    loading,
  };
};

export default useFeatureGate;
